/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { flexRender, type Header, type RowData } from '@tanstack/react-table';
import type { TableFeatureSet } from '../Table.features';

export interface TableHeaderProps<T extends RowData> {
  header: Header<TableFeatureSet, T, unknown>;
  headerProps?: (props: any, { column }: { column: any }) => any[];
  manualSort?: boolean | { sort: string; order: string };
  setManualSort?: (value: string | { sort: string; order: string }) => void;
}

const TableHeader = <T extends RowData>({ header, headerProps, manualSort, setManualSort }: TableHeaderProps<T>) => {
  const canSort = header.column.getCanSort();
  const isSorted = header.column.getIsSorted();
  const sortDirection = isSorted;
  const isSortedDesc = sortDirection === 'desc';
  const isCurrentlySorted = isSorted !== false;

  // Get column meta for backward compatibility
  const columnMeta = header.column.columnDef.meta as any;
  const columnClassName = columnMeta?.className || '';
  const sortBy = columnMeta?.sortBy || header.column.id;
  const sortDisabled = !canSort || columnMeta?.sortDisabled;

  // Handle manual sort state
  let isSorting = isCurrentlySorted;
  let isSortDesc = isSortedDesc;

  if (manualSort && typeof manualSort === 'object') {
    isSorting = (manualSort as any)['sort'] === sortBy;
    isSortDesc = (manualSort as any).order === 'desc';
  }

  // Get header props with backward compatibility
  const headerPropsFromHeader = headerProps ? headerProps({}, { column: header.column }) : [{}, { style: {} }];

  // Create sort props for backward compatibility
  let sortProps: any = {};
  if (canSort && !sortDisabled && !manualSort) {
    sortProps = {
      onClick: header.column.getToggleSortingHandler(),
      style: { cursor: 'pointer' },
    };
  }

  return (
    <div
      className={`th ${columnClassName}`}
      role="columnheader"
      {...headerPropsFromHeader[0]}
      style={{
        ...(!columnClassName.includes('ui__selector-column') && {
          ...headerPropsFromHeader[1]?.style,
          position: 'relative',
          flex: `${header.colSpan} 0 ${header.getSize()}px`,
        }),
        minWidth: header.column.columnDef.minSize,
        maxWidth:
          columnClassName.includes('ui__selector-column') || columnClassName.includes('ui__expander-column')
            ? header.getSize()
            : undefined,
      }}
      data-test-component="TableHeader"
      data-test-element="container"
      data-test-key={header.id}
    >
      <div
        onClick={() => {
          if (manualSort && !sortDisabled && setManualSort) {
            const currentOrder = (manualSort as any).order;
            const newSortData = {
              sort: sortBy,
              order: currentOrder === 'asc' ? 'desc' : 'asc',
            };
            // Support both string and object formats for backward compatibility
            setManualSort(newSortData);
          }
        }}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div
          {...sortProps}
          className="header-content"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flex: 1,
            cursor: !sortDisabled ? 'pointer' : 'auto',
          }}
        >
          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
          {!sortDisabled && isSorting && (
            <span className={`ui__sort-indicator ${isSortDesc ? '-sort-desc' : '-sort-asc'}`} />
          )}
        </div>
      </div>
      {header.column.getCanResize() && (
        <div
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          className={`ui__resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            height: '100%',
            width: '5px',
            cursor: 'col-resize',
            userSelect: 'none',
            touchAction: 'none',
          }}
        />
      )}
    </div>
  );
};

export default TableHeader;
